import { geocodeAddress } from './geocodingService';

const OPEN_METEO_FORECAST_URL = 'https://api.open-meteo.com/v1/forecast';
const OPEN_METEO_ARCHIVE_URL = 'https://archive-api.open-meteo.com/v1/archive';

export interface DailyForecast {
  date: string;
  weatherCode: number;
  tempMax: number;
  tempMin: number;
  precipitation: number;
  windSpeedMax: number;
}

export interface WeatherData {
  latitude: number;
  longitude: number;
  past7DaysRainfall: number;
  forecast: DailyForecast[];
}

const RAIN_ALERT_THRESHOLD_MM = 10;

export const isRainAlert = (precipitation: number): boolean => precipitation > RAIN_ALERT_THRESHOLD_MM;

const weatherCodeMap: Record<number, { label: string; icon: string }> = {
  0: { label: 'Clear', icon: 'sun' },
  1: { label: 'Mainly Clear', icon: 'sun' },
  2: { label: 'Partly Cloudy', icon: 'cloud-sun' },
  3: { label: 'Overcast', icon: 'cloud' },
  45: { label: 'Fog', icon: 'cloud-fog' },
  48: { label: 'Rime Fog', icon: 'cloud-fog' },
  51: { label: 'Light Drizzle', icon: 'cloud-drizzle' },
  53: { label: 'Drizzle', icon: 'cloud-drizzle' },
  55: { label: 'Heavy Drizzle', icon: 'cloud-drizzle' },
  56: { label: 'Freezing Drizzle', icon: 'cloud-drizzle' },
  57: { label: 'Freezing Drizzle', icon: 'cloud-drizzle' },
  61: { label: 'Light Rain', icon: 'cloud-rain' },
  63: { label: 'Rain', icon: 'cloud-rain' },
  65: { label: 'Heavy Rain', icon: 'cloud-rain' },
  66: { label: 'Freezing Rain', icon: 'cloud-rain' },
  67: { label: 'Freezing Rain', icon: 'cloud-rain' },
  71: { label: 'Light Snow', icon: 'cloud-snow' },
  73: { label: 'Snow', icon: 'cloud-snow' },
  75: { label: 'Heavy Snow', icon: 'cloud-snow' },
  77: { label: 'Snow Grains', icon: 'cloud-snow' },
  80: { label: 'Light Showers', icon: 'cloud-drizzle' },
  81: { label: 'Showers', icon: 'cloud-rain' },
  82: { label: 'Violent Showers', icon: 'cloud-rain' },
  85: { label: 'Snow Showers', icon: 'cloud-snow' },
  86: { label: 'Snow Showers', icon: 'cloud-snow' },
  95: { label: 'Thunderstorm', icon: 'cloud-lightning' },
  96: { label: 'Thunderstorm w/ Hail', icon: 'cloud-lightning' },
  99: { label: 'Thunderstorm w/ Hail', icon: 'cloud-lightning' },
};

export const getWeatherInfo = (code: number): { label: string; icon: string } => {
  return weatherCodeMap[code] || { label: 'Unknown', icon: 'cloud' };
};

const formatDateParam = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

const fetchForecast = async (lat: number, lon: number): Promise<DailyForecast[]> => {
  const params = new URLSearchParams({
    latitude: lat.toString(),
    longitude: lon.toString(),
    daily: ['weather_code', 'temperature_2m_max', 'temperature_2m_min', 'precipitation_sum', 'wind_speed_10m_max'].join(','),
    timezone: 'Australia/Sydney',
    forecast_days: '7',
  });

  const res = await fetch(`${OPEN_METEO_FORECAST_URL}?${params}`);
  if (!res.ok) throw new Error('Forecast service unavailable');
  const data = await res.json();

  const dates: string[] = data.daily.time;
  return dates.map((date, i) => ({
    date,
    weatherCode: data.daily.weather_code[i],
    tempMax: data.daily.temperature_2m_max[i],
    tempMin: data.daily.temperature_2m_min[i],
    precipitation: data.daily.precipitation_sum[i],
    windSpeedMax: data.daily.wind_speed_10m_max[i],
  }));
};

const fetchPast7DaysRainfall = async (lat: number, lon: number): Promise<number> => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 7);

  const params = new URLSearchParams({
    latitude: lat.toString(),
    longitude: lon.toString(),
    daily: 'precipitation_sum',
    start_date: formatDateParam(start),
    end_date: formatDateParam(end),
    timezone: 'Australia/Sydney',
  });

  const res = await fetch(`${OPEN_METEO_ARCHIVE_URL}?${params}`);
  if (!res.ok) throw new Error('Historical weather service unavailable');
  const data = await res.json();

  const sums: number[] = data.daily.precipitation_sum || [];
  return sums.reduce((total, val) => total + (val || 0), 0);
};

export const fetchWeatherForSite = async (
  site: { id: string; name: string; address: string; latitude?: number; longitude?: number }
): Promise<WeatherData> => {
  let lat = site.latitude;
  let lon = site.longitude;

  if (lat == null || lon == null || isNaN(lat) || isNaN(lon)) {
    const geocoded = await geocodeAddress(site.address);
    if (!geocoded) {
      throw new Error('Could not determine coordinates for this site address');
    }
    lat = geocoded.latitude;
    lon = geocoded.longitude;
  }

  const [forecast, past7DaysRainfall] = await Promise.all([
    fetchForecast(lat, lon),
    fetchPast7DaysRainfall(lat, lon),
  ]);

  return { latitude: lat, longitude: lon, past7DaysRainfall, forecast };
};
